import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import loadable from '@loadable/component';
import { InternalLink } from '../../../form-elements/Link';
import { isUIkitReadySelector } from '../../../../../modules/selectors/UIkit';
import { Offcanvas } from '../../../Offcanvas/Offcanvas';
import Button from '../../../form-elements/Button';
import { categoriesSelector } from '../../../../../modules/selectors/standort/categories';
import LinkSelector from '../../../Header/LinkSelector';
import ShopMenuList from '../../../Header/ShopMenuList';
import { carDealerLinkSelector, heroContentByPageSelector } from '../../../../../modules/selectors/standort/theme';
import { pageSelector } from '../../../../../modules/selectors/url';
import Image, { BackgroundImage } from '../../../Image/Image';
import HolidayBanner from '../../../Header/HolidayBanner';
import { markenStandortSelector } from '../../../../../modules/selectors/standort/markenStandort';
import { apiClient } from '../../../../../helpers/apiHelperV2';
import useSWR from 'swr';
import { saveCouponBannerHeight } from '../../../../../modules/actions/couponBannerHeight';
import { standortUrlSelector } from '../../../../../modules/selectors/url';
import { setServiceNavigatorStatus } from '../../../../../modules/actions/standort/service/serviceNavigator';
import { MobileMenu } from '../../../MobileMenu/MobileMenu';

const CouponBanner = loadable(() => import('../../../CouponBanner/Couponbanner'));

function NOALogo() {
    return (
            <Image
                src={`https://techno-images.s3.eu-central-1.amazonaws.com/standort-images/035/000/LogoHeader.png`}
                alt={`NOA Logo`}
                resizeHeight={30}
            />
    );
}

function LinksList({ filteredCategories, closeSidebar, focusBtnRef }) {
    const carDealerLink = useSelector(carDealerLinkSelector);
    const markenStandort = useSelector(markenStandortSelector);
    const dispatch = useDispatch();


    const { data: serviceNavigatorstatus } = useSWR(`/api/v1/getServicenavigatorStatus?standortId=${markenStandort._id}`, async url => {
        const { data } = await apiClient.get(url);
        return data;
    });

    const serviceNavigatorStatus = useMemo(() => serviceNavigatorstatus?.status ?? [], [serviceNavigatorstatus]);

    if (serviceNavigatorStatus !== undefined) {
        dispatch(setServiceNavigatorStatus(serviceNavigatorStatus));
    } 

    return (
        <ul className="uk-nav uk-nav-default">
            <li>
                <div ref={focusBtnRef} tabIndex={0}><LinkSelector type={carDealerLink.type} to={carDealerLink.to} label={carDealerLink.label}  /></div>
            </li>
            <li className="uk-active">
                <strong>
                    <span className='uk-text-bold menu-noa' onClick={closeSidebar}>Menü</span>
                </strong>
                <ShopMenuList categories={filteredCategories} closeSidebar={closeSidebar} data-uk-nav="" />
            </li>
            {serviceNavigatorStatus && (
                <li>
                    <strong>
                        <LinkSelector
                            to='/servicenavigator'
                            label='Service-Navigator'
                            type='internalLink'
                        />
                    </strong>
                </li>
            )}
        </ul>
    );
}

function NOAHeader({ links = [] }) {
    const isUIkitReady = useSelector(isUIkitReadySelector);
    const heroContentByPage = useSelector(heroContentByPageSelector);
    const page = useSelector(pageSelector);
    const categories = useSelector(categoriesSelector);
    const markenStandort = useSelector(markenStandortSelector);
    const carDealerLink = useSelector(carDealerLinkSelector);
    const standortUrl = useSelector(standortUrlSelector);
    const coupons = useSelector(state => state.standort[standortUrl].coupons);
    const [couponBannerActive, setCouponBannerActive] = useState(false);
    const dispatch = useDispatch();
    const focusBtnRef = useRef();

    const filterSpecialCategories = useCallback((categoryList) => {
        if (!categoryList) return [];
        
        return categoryList
            .filter(cat => ('isSpecial' in cat && !cat.isSpecial) || !('isSpecial' in cat))
            .map(cat => {
                if (cat.subCategories && cat.subCategories.length > 0) {
                    return {
                        ...cat,
                        subCategories: filterSpecialCategories(cat.subCategories)
                    };
                }
                return cat;
            });
    }, []);
    
    const filteredCategories = useMemo(() => {
        if (!categories) return [];
        if('showSpecialCategories' in markenStandort && !markenStandort.showSpecialCategories) {
            return categories;
        }
        return filterSpecialCategories(categories);
    }, [categories, markenStandort, filterSpecialCategories]);

    const { data: serviceNavigatorstatus } = useSWR(`/api/v1/getServicenavigatorStatus?standortId=${markenStandort._id}`, async url => {
        const { data } = await apiClient.get(url);
        return data;
    });

    const serviceNavigatorStatus = useMemo(() => serviceNavigatorstatus?.status ?? [], [serviceNavigatorstatus]);

    if (serviceNavigatorStatus !== undefined) {
        dispatch(setServiceNavigatorStatus(serviceNavigatorStatus));
    } 

    const [isMobileSidebarVisible, setIsMobileSidebarVisible] = useState(false);
    const [isDesktopSidebarVisible, setIsDesktopSidebarVisible] = useState(false);

    const { desktopLinks, mobileLinks } = useMemo(() => {
        const desktopLinks = links.filter(link => link.icon);
        const mobileLinks = links.filter(link => !link.icon);
        return { desktopLinks, mobileLinks };
    }, [links]);

    const heroContent = useMemo(() => {
        return heroContentByPage.find(hero => hero.page === page);
    }, [page, heroContentByPage]);

    useEffect(() => {
        if (isUIkitReady) {
            window.UIkit.util.ready(function () {
                const navigation = document.querySelector('.tm-header .uk-navbar-container');
                const lastOffset = window.pageYOffset || document.documentElement.scrollTop;
                if (lastOffset > 20) {
                    navigation?.classList.add('jc-nav--toggled');
                } else {
                    navigation?.classList.remove('jc-nav--toggled');
                }
                window.addEventListener('scroll', function () {
                    const offset = window.pageYOffset || document.documentElement.scrollTop;
                    if (offset > 20) {
                        navigation?.classList.add('jc-nav--toggled');
                    } else {
                        navigation?.classList.remove('jc-nav--toggled');
                    }
                });
            });
        }
    }, [isUIkitReady]);

    const checkIfCouponBannerActive = () => {
        return coupons?.data?.some(coupon => { return coupon.banner.isActive === true })
    }

    useEffect(() => {
        let isActive = checkIfCouponBannerActive();
        setCouponBannerActive(isActive);
        dispatch(saveCouponBannerHeight(isActive ? 36 : 0));
    }, [coupons]);

    useEffect(() => {
        if ((isDesktopSidebarVisible || isMobileSidebarVisible) && focusBtnRef.current) {
            focusBtnRef.current.focus();
        }
      }, [isDesktopSidebarVisible, isMobileSidebarVisible]);

    let handlePress = (e) =>{
        e.preventDefault()
        if(e.key === "Enter"){
            setIsDesktopSidebarVisible(false)
            setIsMobileSidebarVisible(false)
        }
    }

    return (
        <div id="custom-theme-noa--header">
            <div className="tm-header-mobile uk-hidden@m">
                <div className="uk-navbar-container">
                    <nav data-uk-navbar="container: .tm-header-mobile" className="uk-navbar">
                        <div className="uk-padding uk-padding-remove-bottom uk-padding-remove-top uk-navbar-left">
                            <InternalLink className="uk-navbar-item uk-padding-remove-left uk-logo noa-outline" to="/" ariaLabel="Startseite">
                                <NOALogo />
                            </InternalLink>
                        </div>
                        <div className="uk-navbar-right">
                            <Button className="uk-navbar-toggle" modifier="link" onClick={() => setIsMobileSidebarVisible(true)}>
                                <div data-uk-navbar-toggle-icon="" className="uk-icon uk-navbar-toggle-icon" aria-hidden="true"/>
                            </Button>
                        </div>
                    </nav>
                </div>
            </div>

            <div className="tm-header uk-visible@m tm-header-overlay uk-header" data-uk-header="">
                <div data-uk-sticky="media: @m; cls-active: uk-navbar-sticky; cls-inactive: uk-navbar-transparent" className="uk-sticky">
                    <div className="uk-navbar-container uk-navbar-transparent uk-light">
                        <div className="uk-container">
                            <nav className="uk-navbar" data-uk-navbar="" aria-label="Hauptmenü">
                                <div className="uk-navbar-left">
                                    <InternalLink className="uk-navbar-item uk-padding-remove-left uk-logo noa-outline" to="/" ariaLabel="Startseite">
                                        <NOALogo />
                                    </InternalLink>
                                </div>
                                <div className="uk-navbar-right">
                                    <div className="uk-navbar-right--toolbar">
                                        <div
                                            className="noa-nav uk-section-default uk-section uk-padding-remove-top uk-padding-remove-bottom"
                                            style={{ backgroundColor: 'transparent' }}
                                        >
                                            <div className="uk-container">
                                                <div
                                                    className="uk-animation-fade tm-grid-expand uk-child-width-1-1 uk-grid-margin uk-grid uk-grid-stack"
                                                    data-uk-grid=""
                                                >
                                                    <div className="uk-first-column">
                                                        <div className="uk-margin uk-text-right">
                                                            <div
                                                                className="uk-flex-middle uk-grid-column-medium uk-grid-row-small uk-child-width-auto uk-flex-right uk-grid"
                                                                data-uk-grid=""
                                                            >
                                                                {desktopLinks.map(({ type, icon, to, label }, index) => (
                                                                    <div key={index} className="el-item">
                                                                        <LinkSelector
                                                                            to={to}
                                                                            label={
                                                                                <>
                                                                                    <span
                                                                                        className="uk-icon"
                                                                                        dangerouslySetInnerHTML={{ __html: icon }}
                                                                                        aria-hidden="true"
                                                                                    />
                                                                                    <span className="uk-text-middle">{label}</span>
                                                                                </>
                                                                            }
                                                                            type={type}
                                                                            className="el-content noa-outline"
                                                                            ariaLabel={label}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <Button className="uk-navbar-toggle noa-outline" modifier="link" onClick={() => setIsDesktopSidebarVisible(true)} ariaLabel="Menü öffnen">
                                        <span className="uk-margin-small-right">Menü</span>
                                        <i data-uk-navbar-toggle-icon="" className="uk-icon uk-navbar-toggle-icon" aria-hidden="true"/>
                                    </Button>
                                </div>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
            <div className="tm-header uk-section-default" data-tm-header-transparent="light">
                {heroContent ? (
                    <BackgroundImage
                        src={heroContent.imageLink}
                        className="uk-background-norepeat uk-background-cover uk-background-center-center uk-section uk-section-xlarge uk-padding-remove-bottom"
                    >
                        <div className="uk-container">
                            <div className="tm-grid-expand uk-grid-margin uk-grid" data-uk-grid="">
                                <div className="uk-grid-item-match uk-width-1-2@m uk-first-column">
                                    <div className="uk-tile-secondary uk-tile uk-tile-small uk-flex uk-flex-bottom">
                                        <div className="uk-panel uk-width-1-1">
                                            <h1> {heroContent.headline} </h1>
                                            <hr className="uk-divider-small" />
                                            <div className="uk-panel uk-margin">{heroContent.description}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="uk-width-1-2@m" />
                            </div>
                        </div>
                    </BackgroundImage>
                ) : (
                    <div className="uk-section uk-section-small uk-visible@m" />
                )}
            </div>
            {/* <Offcanvas
                isVisible={isMobileSidebarVisible}
                onIsVisibleChange={visible => setIsMobileSidebarVisible(visible)}
                mode="slide"
                flip={true}
                overlay
            >
                <div className="uk-offcanvas-bar">
                    <div className="uk-child-width-1-1 uk-grid uk-grid-stack" data-uk-grid="">
                        <div>
                            <div className="uk-panel">
                                <LinksList mobileLinks={mobileLinks} focusBtnRef={focusBtnRef} closeSidebar={() => setIsMobileSidebarVisible(false)} />
                            </div>
                        </div>
                    </div>
                </div>
            </Offcanvas> */}
            <Offcanvas isVisible={isMobileSidebarVisible} onIsVisibleChange={visible => setIsMobileSidebarVisible(visible)}>
                <div className="uk-offcanvas-bar mobile-full-modal" style={{width:"100%", padding:0}}>
                    <div style={{padding: 15}}>
                        <button className="uk-offcanvas-close focus" type="button" ref={focusBtnRef} data-uk-close="" onKeyPress={(e) => handlePress(e)} onClick={() => setIsMobileSidebarVisible(false)} aria-label='Navigation schließen' />
                        <span className='uk-text-bold menu-black'><strong style={{fontWeight: "bold !important", color: "#e2e2e2"}}>Menü</strong></span>
                    </div>
                    <MobileMenu markenStandort={markenStandort} categories={filteredCategories} setIsNavSidebarVisible={setIsMobileSidebarVisible}>                
                    <>
                            <li className="mobile-menu-category">
                                <div ref={focusBtnRef} tabIndex={0}><LinkSelector type={carDealerLink.type} to={carDealerLink.to} label={carDealerLink.label}  /></div>
                            </li>
                            {serviceNavigatorStatus && (
                                <li className="mobile-menu-category" onClick={() => setIsMobileSidebarVisible(false)}>
                                    <strong>
                                        <LinkSelector
                                            to='/servicenavigator'
                                            label='Service-Navigator'
                                            className="uk-text-bold strong-text"
                                            type='internalLink'
                                        />
                                    </strong>
                                </li>
                            )}
                        </>
                    </MobileMenu>
                </div>
                
            </Offcanvas>
            <Offcanvas
                isVisible={isDesktopSidebarVisible}
                onIsVisibleChange={visible => setIsDesktopSidebarVisible(visible)}
                mode="slide"
                flip={true}
                overlay
            >
                <div className="uk-offcanvas-bar uk-flex uk-flex-column">
                    <button className="uk-offcanvas-close uk-close-large uk-icon uk-close focus-white" type="button" data-uk-close="" aria-label='Navigation schließen' />
                    <div className="uk-margin-top widget-menu" id="widget-nav_menu-14">
                        <LinksList filteredCategories={filteredCategories} mobileLinks={mobileLinks} focusBtnRef={focusBtnRef} closeSidebar={() => setIsDesktopSidebarVisible(false)} />
                    </div>
                </div>
            </Offcanvas>
            <HolidayBanner />
            {couponBannerActive && (
                <div  data-uk-sticky="" className='banner-wrapper'>
                    <div className='couponBanner'>
                        <CouponBanner />
                    </div>
                </div>
            )}


        </div>
    );
}

export default NOAHeader;
